import {useFocusManagement} from '@aboutyou/a11y';
import {useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useHistory, useOrdersOverviewRoute} from '@aboutyou/router';
import {
  GenericIconName,
  Icon,
  IconSize,
  Overlay,
  OverlayArrow,
  UnstyledButton,
  Z_INDEXES,
  desktopAndUpLinaria,
  useKeyPress,
  useMatchesMediaQuery,
  useValueAfterHydration,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {useAuthenticationPrompt} from '../../../../features/Authentication/AuthenticationProvider/AuthenticationProvider';
import {useIsAuthenticated} from '../../../../features/Authentication/hooks/useIsAuthenticated';
import {IconMetaNavigationItem} from '../IconMetaNavigationItem';
import {FlyoutLoggedIn} from './FlyoutLoggedIn';
import {FlyoutLoggedOut} from './FlyoutLoggedOut';

const iconSize = 'XSS';

export const IconMetaNavigationItemUser: React.FC<{
  onClick: React.MouseEventHandler;
}> = ({onClick}) => {
  const [openOverlayPopup, setOpenOverlayPopup] = useState(false);
  const {t} = useTranslation();
  const history = useHistory();
  // TODO: remove the usage of `useValueAfterHydration` https://aboutyou.atlassian.net/browse/ASFE-11723
  const isAuthenticated = useValueAfterHydration(
    useIsAuthenticated(),
  )?.isAuthenticated;

  const supportsHover = useMatchesMediaQuery('(pointer: fine)');
  const ordersOverviewRoute = useOrdersOverviewRoute();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const hydrationDone = useHydrationDone();
  const [enableBankDataRequest, setEnableBankDataRequest] = useState(false);

  const closeOverlayPopup = () => {
    setOpenOverlayPopup(prev => !prev);
  };

  const {focusManagementRefCallback, isTopMostPopover} = useFocusManagement({
    isOpen: openOverlayPopup,
  });

  useKeyPress({
    key: 'Escape',
    onKeyDown: closeOverlayPopup,
    disabled: !openOverlayPopup || isTopMostPopover === false,
  });

  return (
    <Container
      onMouseEnter={() => {
        if (!enableBankDataRequest && isAuthenticated) {
          setEnableBankDataRequest(true);
        }
      }}
      data-is-open={openOverlayPopup}
    >
      <IconAndOpenerWrapper>
        <StyledIconMetaNavigationItem
          dataTestId="UserAccount"
          dataAttributes={{'data-dd-privacy': 'mask'}}
          icon={GenericIconName.NavUser}
          label={isAuthenticated ? t('Account') : t('Login')}
          onClick={event => {
            onClick(event);

            if (isAuthenticated) {
              if (supportsHover) {
                history.push(ordersOverviewRoute);
              }
            } else {
              showUserAuthenticationPrompt({
                context: 'desktop-header-login',
                needsCheckoutSession: false,
              });
            }
          }}
        />
        {hydrationDone && isAuthenticated && (
          <MenuOpener
            aria-label={t('Open user menu window')}
            onClick={closeOverlayPopup}
          >
            <Icon asset={GenericIconName.ArrowDown} size={iconSize} />
          </MenuOpener>
        )}
      </IconAndOpenerWrapper>
      {hydrationDone && (isAuthenticated || supportsHover) && (
        <>
          <StyledOverlayArrow />
          <OverlayPopup ref={focusManagementRefCallback()}>
            <StyledOverlay>
              {isAuthenticated ? (
                <FlyoutLoggedIn
                  enableBankDataRequest={enableBankDataRequest}
                  onLogout={() => setEnableBankDataRequest(false)}
                />
              ) : (
                <FlyoutLoggedOut />
              )}
            </StyledOverlay>
          </OverlayPopup>
        </>
      )}
    </Container>
  );
};

// Added explicitly to position it centered to the trigger
const StyledOverlayArrow = styled(OverlayArrow)`
  display: none;
  top: calc(100% - 8px - 0.5rem);
  z-index: ${Z_INDEXES.TADARIDA_HEADER_ICON_META_NAVIGATION_CONTENT + 1};

  [data-is-open='true'] & {
    display: block;
  }
`;

// Used to add padding around the overlay to not create a gap that
// would cause the overlay to close when the mouse leaves the trigger
const OverlayPopup = styled.div`
  display: none;
  position: absolute;
  top: calc(100% - 7px - 0.5rem);
  right: -0.5rem;
  z-index: ${Z_INDEXES.TADARIDA_HEADER_ICON_META_NAVIGATION_CONTENT};
  padding: 0.5rem;

  [data-is-open='true'] & {
    display: block;
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  height: 100%;

  &:hover {
    ${OverlayPopup}, ${StyledOverlayArrow} {
      display: block;
    }
  }
`;

const StyledOverlay = styled(Overlay)`
  padding: 0;
`;

const MenuOpener = styled(UnstyledButton)`
  --icon-extra-space: 2px;
  display: flex;
  align-self: center;
  position: absolute;
  /* prettier-ignore */
  right: calc(-1 * (var(--icon-extra-space) + ${IconSize[
    iconSize
  ]}px)); // the size of the icon plus 2px for space
  bottom: 0;
  margin: auto;
  opacity: 0;
  top: 0;
  transition: opacity 0.2s ease;

  &:focus-visible {
    opacity: 1;
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  &:not(:focus-visible) {
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  ${desktopAndUpLinaria(`
    --icon-extra-space: 5px;
  `)}
`;

const StyledIconMetaNavigationItem = styled(IconMetaNavigationItem)`
  &:focus-visible + ${MenuOpener} {
    opacity: 1;
  }
`;

const IconAndOpenerWrapper = styled.div`
  position: relative;
`;
